/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import { Select, Box, Button, theme, FlexContainer } from "@nordcloud/gnui";
import { DiscoveryRuleMatch } from "~/generated/graphql";
import { useDisclosure } from "~/hooks";
import { KeyValue } from "~/models";
import { AddResourceGroupForm, AddTagForm } from "../components";
import { SELECT_LABEL, SELECT_OPTIONS } from "../constants";
import { RuleType, SelectInputOption } from "../types";
import { AwsSecurityGroupSelector } from "./AwsSecurityGroupSelector/AwsSecurityGroupSelector";
import { CloudAccountSelector } from "./CloudAccountSelector";
import { GcpProjectSelector } from "./GcpProjectSelector";
import { ResourceTypeSelector } from "./ResourceTypeSelector";
import { VpcSubnetSelector } from "./VpcSubnetSelector";

type Props = {
  onSubmit: (
    id: string,
    rule: DiscoveryRuleMatch,
    ibmResourceGroup?: boolean
  ) => void;
  handleDelete: (
    ruleType: string,
    ruleValue: KeyValue | string | undefined
  ) => void;
  id?: string;
  excludeOptions?: string[];
  onClose?: () => void;
};

export function AddRuleForm({
  onSubmit,
  id = "",
  excludeOptions = [],
  onClose,
  handleDelete,
}: Props) {
  const { isOpen, close, open } = useDisclosure();

  const [activeSelector, setActiveSelector] = useState<RuleType | null>(null);

  const handleSelectChange = (item: SelectInputOption) => {
    setActiveSelector(item.value);
    open();
  };

  const handleSubmit = (item: KeyValue | string) => {
    if (activeSelector == null) {
      return;
    }

    if (activeSelector === RuleType.IBM_RESOURCE_GROUP) {
      onSubmit(
        id,
        {
          [RuleType.RESOURCE_GROUP]: [item as string],
        },
        true
      );
    } else {
      onSubmit(id, {
        [activeSelector]: [item],
      });
    }
    close();
  };

  const handleMultiSubmit = (items: string[], removeItems: string[]) => {
    if (activeSelector == null) {
      return;
    }

    if (activeSelector === RuleType.SECURITY_GROUP_IDS) {
      items.forEach((item) => {
        if (!excludeOptions.includes(item)) {
          onSubmit(id, {
            [RuleType.SECURITY_GROUP_IDS]: [item],
          });
        }
      });

      removeItems.forEach((item) => {
        handleDelete(RuleType.SECURITY_GROUP_IDS, item);
      });
    }

    close();
  };

  const deleteSubnets = (subnetIds: string[]) => {
    if (activeSelector === RuleType.AWS_VPC) {
      subnetIds.forEach((subnetId) => handleDelete(activeSelector, subnetId));
    }
  };

  const options = SELECT_OPTIONS.filter((option) => {
    return !excludeOptions.includes(option.label);
  });

  const isTagFormActive = activeSelector === RuleType.RESOURCE_TAGS;
  const isResourceGroupFormActive = activeSelector === RuleType.RESOURCE_GROUP;
  const isResourceGroupFormIbmActive =
    activeSelector === RuleType.IBM_RESOURCE_GROUP;
  const isGcpProjectSelectorActive = activeSelector === RuleType.PROJECT_ID;
  const isResourceTypeSelectorActive =
    activeSelector === RuleType.RESOURCE_TYPE;
  const isCloudAccountSelectorActive =
    activeSelector === RuleType.CLOUD_ACCOUNT;
  const isVpcSubnetSelectorActive = activeSelector === RuleType.AWS_VPC;
  const isSecurityGroupSelectorActive =
    activeSelector === RuleType.SECURITY_GROUP_IDS;

  return (
    <>
      <Box
        boxStyle="lightGrey"
        innerSpacing="spacing02"
        spacing="spacing02"
        data-testid="add-rule-form"
      >
        <FlexContainer
          css={{
            marginBottom: theme.spacing.spacing02,
            justifyContent: "space-between",
          }}
        >
          <label htmlFor={id}>{SELECT_LABEL}</label>
          {onClose != null && (
            <Button icon="close" size="sm" severity="low" onClick={onClose} />
          )}
        </FlexContainer>
        <Select
          options={options}
          id={id}
          onChange={(value) => handleSelectChange(value as SelectInputOption)}
        />
      </Box>
      <AddTagForm
        isOpen={isOpen && isTagFormActive}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <AddResourceGroupForm
        isIbmResourceGroup={isResourceGroupFormIbmActive}
        isOpen={
          isOpen && (isResourceGroupFormActive || isResourceGroupFormIbmActive)
        }
        onSubmit={handleSubmit}
        onClose={close}
      />
      <GcpProjectSelector
        isOpen={isOpen && isGcpProjectSelectorActive}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <ResourceTypeSelector
        isOpen={isOpen && isResourceTypeSelectorActive}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <CloudAccountSelector
        isOpen={isOpen && isCloudAccountSelectorActive}
        onSubmit={handleSubmit}
        onClose={close}
      />
      <AwsSecurityGroupSelector
        isOpen={isOpen && isSecurityGroupSelectorActive}
        onSubmit={handleMultiSubmit}
        onClose={close}
      />
      <VpcSubnetSelector
        handleDelete={deleteSubnets}
        isOpen={isOpen && isVpcSubnetSelectorActive}
        onSubmit={handleSubmit}
        onClose={close}
      />
    </>
  );
}
